import { FunctionComponent, memo, useContext } from "react";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import Axios from "axios";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { Button } from "@edgetier/components";
import { ISpellingExemptionGetResponse, Route } from "@edgetier/watchtower-types";
import { faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { getTimeToRead } from "@edgetier/utilities";
import { axios } from "utilities/axios";
import { getServerError } from "utilities/get-server-error";
import { IProps } from "./spelling-exemption-remove-action.types";
import { removeSpellingExemptionFromCache } from "./spelling-exemption-remove-action.utilities";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import "./spelling-exemption-remove-action.scss";

/**
 * Button component, when clicked removes an exemption from table
 * @param props.exemption   Currently selected exemption
 */
const SpellingExemptionRemoveAction: FunctionComponent<IProps> = ({ exemption }) => {
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { openModal, closeModal, modalProps } = useModal({ onCloseModal: unlock });
    const queryClient = useQueryClient();

    const onRemoveExemption = async () => {
        try {
            await axios.delete(Route.SpellingExemption.replace(":spellingExemptionId", exemption.spellingExemptionId));

            // Remove the exemption from cache
            queryClient.setQueriesData<ISpellingExemptionGetResponse>(
                Route.SpellingExemptions,
                removeSpellingExemptionFromCache(exemption)
            );

            const successMessage = `Successfully removed ${exemption.exemption}`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
        } catch (error) {
            if (Axios.isAxiosError(error)) {
                toast.error(getServerError(error));
            } else {
                const errorMessage = "Failed to remove an exemption";
                toast.error(errorMessage, { duration: getTimeToRead(errorMessage, 1000) });
            }
        } finally {
            closeModal();
        }
    };
    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Delete exemption</h2>
                </ModalHeader>
                <ModalContent>
                    <div className="remove-spelling-exemption">
                        <p>Are you sure you want to remove &ldquo;{exemption.exemption}&rdquo;?</p>

                        <div className="remove-spelling-exemption__button">
                            <Button className="" icon={faTrashAlt} styleName="negative" onClick={onRemoveExemption}>
                                Remove exemption
                            </Button>
                        </div>
                    </div>
                </ModalContent>
            </Modal>

            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faTrashAlt}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Remove
            </EllipsisMenuItem>
        </>
    );
};

export default memo(SpellingExemptionRemoveAction);
